import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VSLQualifyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", niche: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Apply — BrandChilly High-Ticket Marketing Funnel";
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "VSLQualify", content_category: "VSL Funnel" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "VSLQualify" });
    } catch (e) {
      // swallow
    }
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.niche.trim()) {
      toast.error("Please fill in all the fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        niche: form.niche.trim(),
        message: "VSL qualification — requested one-pager on the marketing funnel offer",
        source: "vsl_qualify",
      });
      try {
        if (window.fbq) window.fbq("track", "Lead", { content_name: "VSLQualify", source: "qualification_form" });
        if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR", source: "vsl_qualification" });
      } catch (err) { /* swallow */ }
      navigate("/vsl/video");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-24 md:h-32 flex items-center justify-center">
          <img src="/assets/logo.png" alt="BrandChilly" className="h-20 md:h-28 w-auto" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20 text-center">
        <div
          className="inline-block c-eyebrow text-[10px] md:text-[11px] mb-6 px-3 py-1.5 border border-red-700/40 text-red-400"
          style={{ borderRadius: 0 }}
          data-testid="vsl-qualify-eyebrow"
        >
          For Coaches, Freelancers &amp; Agencies ONLY
        </div>

        <h1
          className="text-[34px] sm:text-5xl md:text-6xl leading-[1.05] text-white"
          style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}
          data-testid="vsl-qualify-headline"
        >
          How I Enrol <span className="c-red italic">50 High-Ticket Clients</span> Every Month
          <span className="block mt-3 text-zinc-400 text-2xl sm:text-3xl md:text-4xl font-normal italic">
            (And how you can copy our marketing funnel to get higher-quality leads — or you don't pay)
          </span>
        </h1>

        <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg c-muted leading-relaxed">
          Without doing webinars, bidding wars, cold emailing, replying to hundreds of comments,
          or sliding into the DMs.
        </p>

        <div className="mt-12 md:mt-14 max-w-md mx-auto text-left">
          <p className="text-center text-sm md:text-base text-white mb-6 italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Enter your information below and we'll send you a one-pager on how this offer works.
          </p>

          <form onSubmit={submit} className="space-y-3" data-testid="vsl-qualify-form">
            <input
              type="text"
              placeholder="Full Name"
              value={form.name}
              onChange={update("name")}
              required
              className="w-full bg-transparent border border-white/15 text-white placeholder:text-zinc-500 px-4 py-3.5 text-base focus:border-red-600 outline-none transition"
              style={{ borderRadius: 0 }}
              data-testid="vsl-qualify-name"
            />
            <input
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={update("email")}
              required
              className="w-full bg-transparent border border-white/15 text-white placeholder:text-zinc-500 px-4 py-3.5 text-base focus:border-red-600 outline-none transition"
              style={{ borderRadius: 0 }}
              data-testid="vsl-qualify-email"
            />
            <input
              type="tel"
              placeholder="WhatsApp Number (with country code)"
              value={form.phone}
              onChange={update("phone")}
              required
              className="w-full bg-transparent border border-white/15 text-white placeholder:text-zinc-500 px-4 py-3.5 text-base focus:border-red-600 outline-none transition"
              style={{ borderRadius: 0 }}
              data-testid="vsl-qualify-phone"
            />
            <input
              type="text"
              placeholder="What do you coach / freelance / agency in?"
              value={form.niche}
              onChange={update("niche")}
              required
              className="w-full bg-transparent border border-white/15 text-white placeholder:text-zinc-500 px-4 py-3.5 text-base focus:border-red-600 outline-none transition"
              style={{ borderRadius: 0 }}
              data-testid="vsl-qualify-niche"
            />

            <button
              type="submit"
              disabled={submitting}
              className="c-btn-primary w-full justify-center text-base md:text-lg py-4 mt-4 disabled:opacity-60"
              data-testid="vsl-qualify-submit"
            >
              {submitting ? (
                <><Loader2 size={18} className="animate-spin" /> Submitting…</>
              ) : (
                <>Get Started <ArrowRight size={18} /></>
              )}
            </button>

            <p className="text-center text-[11px] tracking-[0.12em] uppercase text-zinc-600 pt-3">
              Your details stay private. No spam.
            </p>
          </form>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center text-[11px] text-zinc-600 tracking-[0.18em] uppercase">
          BrandChilly &middot; Personal Branding &amp; Business Growth &middot; India
        </div>
      </footer>
    </div>
  );
}
